import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  DeleteItAssetDocument,
  ItAssetDetailsQuery,
  ItAssetsTableQuery,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

// Union type to handle both table and details assets
type Asset =
  | ItAssetsTableQuery['itManagement']['itAssetsConnection']['nodes'][number]
  | ItAssetDetailsQuery['itManagement']['itAsset']

interface Props {
  asset: Asset
  onClose: () => void
}

export const DeleteItAssetModal = ({ asset, onClose }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { navigation } = useNavigation()
  const [deleteItAsset, { loading: deleteLoading }] = useMutation(DeleteItAssetDocument)

  const handleDeleteAsset = async () => {
    if (!asset) return

    try {
      await deleteItAsset({
        variables: { id: asset.id },
        refetchQueries: ['ItAssetsTable', 'ItAssetDetails', 'ItAssetsStatusCounts'],
        awaitRefetchQueries: true,
      })
      app.setFlash({
        type: 'correct',
        message: i18n.t('it_management.assets.delete_modal.success_message'),
      })
      onClose()
      // Redirect to inventory page only when deleting from details page
      if (
        location.pathname.includes('/it-management/') &&
        location.pathname.split('/').length > 2
      ) {
        navigate(navigation.it_management.fullPath())
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.assets.delete_modal.error_message'),
      })
    }
  }

  const assetName = asset
    ? asset.itAssetModel?.brand && asset.itAssetModel?.name
      ? `${asset.itAssetModel.brand} ${asset.itAssetModel.name}`
      : asset.serialNumber || 'Unknown Asset'
    : 'Unknown Asset'

  return (
    <Modal
      title={i18n.t('it_management.assets.delete_modal.title')}
      onClose={onClose}
      illustration={'delete-cross'}
      description={i18n.t('it_management.assets.delete_modal.description', { assetName })}
      onSubmit={handleDeleteAsset}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose} disabled={deleteLoading}>
            {i18n.t('common.cancel')}
          </Button>

          <Button type="negative" submit disabled={deleteLoading}>
            {deleteLoading ? i18n.t('common.deleting') : i18n.t('common.destroy')}
          </Button>
        </Box>
      }
    />
  )
}
